import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import LpnCard from 'tio-common/components/lpn/lpn-card';
import pageTitle from 'ember-page-title/helpers/page-title';
import { PhGlobe, PhFaders } from 'ember-phosphor-icons';
import { VStack, Section } from 'tio-ui/components/layout';
import { t, type IntlService } from 'ember-intl';
import { Paginator } from 'tio-ui/components/paginator';
import type { LpnPageExtension as LpnPage } from 'tio-data/data/resources/lpn-page.ext';
import type { CompanySettingExtension as CompanySettingModel } from 'tio-data/data/resources/company-setting.ext';
import type AuthenticatedLpnIndexController from 'tio-employee/controllers/authenticated/lpn/index';
import { service } from '@ember/service';
import Drawer from 'tio-ui/components/drawer';
import Select from 'tio-ui/components/select';
import Button from 'tio-ui/components/button';
import SearchInput from 'tio-ui/components/search-input';
import {
  FORMAT_OPTIONS,
  PROGRAM_OPTIONS as PROGRAMS_OPTIONS,
  STUDY_AREA_OPTIONS as AREAS_OF_STUDY_OPTIONS,
  SAVINGS_OPTIONS,
} from 'tio-common/utils/lpn-options';

interface LpnIndexComponentArgs {
  model: {
    recommendedPartners: LpnPage[];
    allPartners: LpnPage[];
    companySettings: CompanySettingModel;
    totalItems: number;
  };
  controller: AuthenticatedLpnIndexController;
}

export default class LpnIndexComponent extends Component<LpnIndexComponentArgs> {
  @service declare intl: IntlService;

  @tracked isFilterDrawerOpen = false;
  @tracked searchValue = this.args.controller.search ?? '';
  _searchTimer: ReturnType<typeof setTimeout> | null = null;

  static readonly SEARCH_DEBOUNCE_MS = 300;
  static readonly SEARCH_MIN_CHARS = 3;

  get activeFilterCount(): number {
    const c = this.args.controller;
    return (
      (c.savings?.length ?? 0) +
      (c.format?.length ?? 0) +
      (c.programs?.length ?? 0) +
      (c.areasOfStudy?.length ?? 0)
    );
  }

  get filterButtonLabel(): string {
    const count = this.activeFilterCount;
    return count > 0 ? `Filters (${count})` : 'Filters';
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  changePaginatorPage = (page: number) => {
    this.args.controller.page = Math.max(1, page);
  };

  openFilterDrawer = () => {
    this.isFilterDrawerOpen = true;
  };

  closeFilterDrawer = () => {
    this.isFilterDrawerOpen = false;
  };

  handleSearchChange = (value: string) => {
    this.searchValue = value;

    if (this._searchTimer) {
      clearTimeout(this._searchTimer);
    }

    // If cleared or below threshold, reset immediately
    if (value.length === 0) {
      this.applySearch();
      return;
    }

    if (value.length < LpnIndexComponent.SEARCH_MIN_CHARS) {
      return;
    }

    this._searchTimer = setTimeout(() => {
      this.applySearch();
    }, LpnIndexComponent.SEARCH_DEBOUNCE_MS);
  };

  handleSearchClear = () => {
    this.searchValue = '';
    this.applySearch();
  };

  applySearch = () => {
    this.args.controller.search = this.searchValue;
    this.args.controller.page = 1;
  };

  setSavings = (keys: string[]) => {
    this.args.controller.savings = keys;
    this.args.controller.page = 1;
  };

  setFormat = (keys: string[]) => {
    this.args.controller.format = keys;
    this.args.controller.page = 1;
  };

  setPrograms = (keys: string[]) => {
    this.args.controller.programs = keys;
    this.args.controller.page = 1;
  };

  setAreasOfStudy = (keys: string[]) => {
    this.args.controller.areasOfStudy = keys;
    this.args.controller.page = 1;
  };

  clearAllFilters = () => {
    this.args.controller.savings = [];
    this.args.controller.format = [];
    this.args.controller.programs = [];
    this.args.controller.areasOfStudy = [];
    this.args.controller.page = 1;
  };

  <template>
    {{pageTitle (t "lpn.learning_partner_network")}}
    <VStack class="tio-col-flush min-h-full p-4">
      <span class="tio-row-between">
        <div class="tio-row-flush tio-text-2xl-semibold tio-fg-secondary">
          <PhGlobe class="mr-2" />
          {{t "lpn.learning_partner_network"}}
        </div>
        <div class="tio-flex-tight">
          <SearchInput
            @placeholder={{t "lpn.filters.search_placeholder"}}
            @value={{this.searchValue}}
            @onChange={{this.handleSearchChange}}
            @onClear={{this.handleSearchClear}}
          />
          <Button
            @appearance="outlined"
            @label={{this.filterButtonLabel}}
            @icon={{PhFaders}}
            {{on "click" this.openFilterDrawer}}
          />
        </div>
      </span>

      {{#if @model.allPartners.length}}
        <p class="tio-fg-secondary">{{t "lpn.lpn_description"}}</p>
        <div class="mt-1">
          <p class="tio-text-xs-semibold tio-fg-muted">{{t "lpn.lpn_disclaimer_title"}}</p>
          <p class="text-xs tio-fg-muted">{{t "lpn.lpn_disclaimer_body"}}</p>
        </div>
      {{else}}
        <h2>{{t "lpn.no_schools"}}</h2>
      {{/if}}

      {{#if @model.recommendedPartners.length}}
        <Section @headerClass="text-lg font-semibold tio-lpn-section-heading">
          <:header>
            {{t "lpn.recommended"}}
          </:header>
          <:body>
            <div class="grid grid-cols-1 xl:grid-cols-3 gap-x-2 gap-y-2">
              {{#each @model.recommendedPartners as |lpn|}}
                <LpnCard @lpn={{lpn}} @companySettings={{@model.companySettings}} />
              {{/each}}
            </div>
          </:body>
        </Section>
      {{/if}}

      {{#if @model.allPartners.length}}
        <Section @headerClass="text-lg font-semibold tio-lpn-section-heading">
          <:header>
            {{t "lpn.schools_in_network"}}
          </:header>
          <:body>
            <div class="grid grid-cols-1 xl:grid-cols-3 gap-x-2 gap-y-2">
              {{#each @model.allPartners as |lpn|}}
                <LpnCard @lpn={{lpn}} @companySettings={{@model.companySettings}} />
              {{/each}}
            </div>
            <Paginator
              @totalItems={{@model.totalItems}}
              @pageSize={{@controller.limit}}
              @currentPage={{@controller.page}}
              @onChange={{this.changePaginatorPage}}
              class="my-4"
            />
          </:body>
        </Section>
      {{/if}}
    </VStack>

    {{! ── Filter Drawer ─────────────────────────────────────────────────── }}
    <Drawer
      @isOpen={{this.isFilterDrawerOpen}}
      @onClose={{this.closeFilterDrawer}}
      @placement="right"
      @size="sm"
      @manageHistory={{false}}
    >
      <:header>
        <div class="tio-row-between w-full pr-8">
          <h2 class="tio-text-lg-semibold tio-fg-primary">{{t "lpn.filters.title"}}</h2>
          <button
            type="button"
            class="tio-text-sm-medium tio-fg-info hover:tio-fg-info-h"
            {{on "click" this.clearAllFilters}}
          >
            {{t "lpn.filters.clear_all"}}
          </button>
        </div>
      </:header>
      <:body>
        <div class="space-y-6">
          <Select
            @label={{t "lpn.filters.savings"}}
            @items={{this.savingsOptions}}
            @multiple={{true}}
            @selectedKeys={{@controller.savings}}
            @onChangeKeys={{this.setSavings}}
            @placeholder={{t "lpn.filters.savings_placeholder"}}
          />
          <Select
            @label={{t "lpn.filters.format"}}
            @items={{this.formatOptions}}
            @multiple={{true}}
            @selectedKeys={{@controller.format}}
            @onChangeKeys={{this.setFormat}}
            @placeholder={{t "lpn.filters.format_placeholder"}}
          />
          <Select
            @label={{t "lpn.filters.programs"}}
            @items={{this.programsOptions}}
            @multiple={{true}}
            @selectedKeys={{@controller.programs}}
            @onChangeKeys={{this.setPrograms}}
            @placeholder={{t "lpn.filters.programs_placeholder"}}
          />
          <Select
            @label={{t "lpn.filters.areas_of_study"}}
            @items={{this.areasOfStudyOptions}}
            @multiple={{true}}
            @selectedKeys={{@controller.areasOfStudy}}
            @onChangeKeys={{this.setAreasOfStudy}}
            @placeholder={{t "lpn.filters.areas_of_study_placeholder"}}
          />
        </div>
      </:body>
    </Drawer>
  </template>

  // ── Translated option lists ─────────────────────────────────────────

  private _translateOptions(options: { key: string; labelKey: string }[]) {
    return options.map((o) => ({ key: o.key, label: this.intl.t(o.labelKey) }));
  }

  get savingsOptions() {
    return this._translateOptions(SAVINGS_OPTIONS);
  }

  get formatOptions() {
    return this._translateOptions(FORMAT_OPTIONS);
  }

  get programsOptions() {
    return this._translateOptions(PROGRAMS_OPTIONS);
  }

  get areasOfStudyOptions() {
    return this._translateOptions(AREAS_OF_STUDY_OPTIONS);
  }
}
